/*
 * benefits-gallery-2026 — keep decorative <video> elements autoplaying.
 *
 * Shared by 2026 sections: any <video> inside a [data-fs-autoplay-video]
 * wrapper opts in (2026-benefits-gallery, 2026-benefits-gallery-3, ...).
 *
 * The bare `video_tag` autoplay is fragile: Dawn's global pauseAllMedia()
 * (assets/global.js) pauses EVERY <video> whenever a modal/popup opens, and
 * nothing resumes these. This controller:
 *   - asserts muted + inline playback (autoplay-policy safe),
 *   - plays the video while it is in view, pauses it off-screen (power saving),
 *   - re-plays it if something external (pauseAllMedia, tab blur) pauses it
 *     while it is still visible.
 *
 * Loaded once per page (guarded), regardless of how many instances opt in.
 */
(function () {
  'use strict';

  if (window.__benefitsGallery2026) return;
  window.__benefitsGallery2026 = true;

  var SELECTOR = '[data-fs-autoplay-video] video';

  // Respect the visitor's reduce-motion preference: cancel autoplay (including
  // the native attribute set by video_tag) and leave the poster static.
  var REDUCED_MOTION =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function safePlay(video) {
    var promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {
        /* Autoplay can still be rejected (Low Power / Data Saver); ignore. */
      });
    }
  }

  function setup(video) {
    if (video.dataset.bg2026Bound === 'true') return;
    video.dataset.bg2026Bound = 'true';

    if (REDUCED_MOTION) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
      return;
    }

    // Required for programmatic autoplay and to avoid iOS fullscreen takeover.
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');

    var shouldPlay = false;

    // Recover from external pauses (e.g. Dawn's pauseAllMedia() on modal open).
    video.addEventListener('pause', function () {
      if (shouldPlay && document.visibilityState === 'visible') safePlay(video);
    });

    if (!('IntersectionObserver' in window)) {
      shouldPlay = true;
      safePlay(video);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            shouldPlay = true;
            safePlay(entry.target);
          } else {
            shouldPlay = false;
            entry.target.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(video);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach(setup);
  }

  // Re-play in-view videos when the tab becomes visible again.
  document.addEventListener('visibilitychange', function () {
    if (REDUCED_MOTION) return;
    if (document.visibilityState !== 'visible') return;
    document.querySelectorAll(SELECTOR).forEach(function (video) {
      if (video.dataset.bg2026Bound === 'true' && !video.paused) return;
      var rect = video.getBoundingClientRect();
      var inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) safePlay(video);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-scan when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
